/**
 * Bingo types. US-style 5x5 with B-I-N-G-O column letters and a free centre.
 *
 * <p>Not lifted from cs-core (cs-core's boardgame module is for movement-based
 * games — Monopoly-style — not bingo). This is a fresh design that fits the
 * GTC plan's "bingo halls" pillar in Casino Game Land.
 */

export const BINGO_COLUMN_LETTERS = ['B', 'I', 'N', 'G', 'O'] as const;
export type BingoColumnLetter = (typeof BINGO_COLUMN_LETTERS)[number];

/**
 * 5x5 grid of numbers. {@code cells[col][row]} is the cell at column {@code col}
 * (0=B, 1=I, 2=N, 3=G, 4=O), row {@code row} (0=top). The centre cell
 * {@code cells[2][2]} is always {@code 0}, representing the FREE square.
 *
 * <p>Number ranges per column: B 1–15, I 16–30, N 31–45, G 46–60, O 61–75.
 */
export interface BingoCard {
  cells: readonly (readonly number[])[];
}

export interface BingoCallNumber {
  letter: BingoColumnLetter;
  number: number;
}

/** Kind of bingo win. Each kind pays a different multiplier. */
export type BingoWinKind =
  | 'row'
  | 'column'
  | 'diagonal'
  | 'four-corners'
  | 'blackout';

export interface BingoWin {
  kind: BingoWinKind;
  /** Cells that contributed to the win, as {@code [col, row]} pairs. */
  cells: readonly (readonly [number, number])[];
  /** Bet multiplier for this win kind. */
  multiplier: number;
}

export interface BingoPayoutMap {
  row: number;
  column: number;
  diagonal: number;
  'four-corners': number;
  blackout: number;
}

export const DEFAULT_BINGO_PAYOUTS: BingoPayoutMap = {
  row: 5,
  column: 5,
  diagonal: 8,
  'four-corners': 10,
  blackout: 50,
};
